// src/services/drugPanelApi.ts
import { api } from "@/services/api";
import type { TestType, TestTypeRequest } from "@/api/testApi";


export interface DrugPanel {
  id: number;
  panelName: string;
  panelDescription: string;
  components: string[]; // Array of substance names
  numComponents: number;
  aliases: string[]; // Alternative names (stored in testComponents JSON)
  status: "ACTIVE" | "INACTIVE";
  createdAt: string;
  updatedAt: string;
}

export interface DrugPanelRequest {
  panelName: string;
  panelDescription: string;
  components: string[];
  aliases: string[];
  status: "ACTIVE" | "INACTIVE";
}

// Helper functions to convert between DrugPanel and TestType
const testTypeToDrugPanel = (testType: TestType): DrugPanel => {
  let components: string[] = [];
  let aliases: string[] = [];
  
  try {
    if (testType.testComponents) {
      const parsed = JSON.parse(testType.testComponents);
      if (Array.isArray(parsed)) {
        components = parsed;
      } else if (parsed.components && Array.isArray(parsed.components)) {
        components = parsed.components;
        aliases = parsed.aliases || [];
      }
    }
  } catch (e) {
    console.error("Error parsing testComponents:", e);
  }

  return {
    id: testType.id,
    panelName: testType.testName,
    panelDescription: testType.testDescription || "",
    components,
    numComponents: components.length,
    aliases,
    status: testType.status as "ACTIVE" | "INACTIVE",
    createdAt: testType.createdAt,
    updatedAt: testType.updatedAt,
  };
};

const drugPanelToTestTypeRequest = (panel: DrugPanelRequest): TestTypeRequest => {
  const testComponents = JSON.stringify({
    components: panel.components,
    aliases: panel.aliases || [],
  });

  return {
    testName: panel.panelName,
    testDescription: panel.panelDescription,
    testCategory: "Substance Abuse Testing",
    status: panel.status,
    testComponents,
  };
};

export const drugPanelApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listDrugPanels: builder.query<DrugPanel[], { search?: string; component?: string }>({
      query: () => ({
        url: "/super-admin/tests/category/Substance Abuse Testing",
        method: "GET",
      }),
      transformResponse: (response: TestType[], _meta, arg) => {
        let panels = response.map(testTypeToDrugPanel);
        
        // Client-side filtering
        if (arg?.search) {
          const searchLower = arg.search.toLowerCase();
          panels = panels.filter(
            (panel) =>
              panel.panelName.toLowerCase().includes(searchLower) ||
              panel.aliases.some((alias) => alias.toLowerCase().includes(searchLower))
          );
        }
        
        if (arg?.component) {
          const componentLower = arg.component.toLowerCase();
          panels = panels.filter((panel) =>
            panel.components.some((comp) => comp.toLowerCase().includes(componentLower))
          );
        }
        
        return panels;
      },
      providesTags: ["DrugPanels", "Tests"],
    }),

    getDrugPanelById: builder.query<DrugPanel, number>({
      query: (id) => `/super-admin/tests/${id}`,
      transformResponse: (response: TestType) => testTypeToDrugPanel(response),
      providesTags: ["DrugPanels", "Tests"],
    }),

    createDrugPanel: builder.mutation<DrugPanel, DrugPanelRequest>({
      query: (data) => ({
        url: "/super-admin/tests",
        method: "POST",
        body: drugPanelToTestTypeRequest(data),
      }),
      transformResponse: (response: TestType) => testTypeToDrugPanel(response),
      invalidatesTags: ["DrugPanels", "Tests"],
    }),

    updateDrugPanel: builder.mutation<DrugPanel, { id: number; data: Partial<DrugPanelRequest> }>({
      queryFn: async ({ id, data }, _queryApi, _extraOptions, fetchWithBQ) => {
        // First, get the existing test type
        const existingResult = await fetchWithBQ({
          url: `/super-admin/tests/${id}`,
          method: "GET",
        });

        if (existingResult.error) {
          return { error: existingResult.error };
        }

        const existing = existingResult.data as TestType;
        const existingPanel = testTypeToDrugPanel(existing);

        // Merge the data
        const merged: DrugPanelRequest = {
          panelName: data.panelName ?? existingPanel.panelName,
          panelDescription: data.panelDescription ?? existingPanel.panelDescription,
          components: data.components ?? existingPanel.components,
          aliases: data.aliases ?? existingPanel.aliases,
          status: data.status ?? existingPanel.status,
        };

        // Convert to test type request
        const testTypeRequest = drugPanelToTestTypeRequest(merged);

        // Update the test type
        const updateResult = await fetchWithBQ({
          url: `/super-admin/tests/${id}`,
          method: "PUT",
          body: testTypeRequest,
        });

        if (updateResult.error) {
          return { error: updateResult.error };
        }

        return { data: testTypeToDrugPanel(updateResult.data as TestType) };
      },
      invalidatesTags: ["DrugPanels", "Tests"],
    }),

    deleteDrugPanel: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/super-admin/tests/${id}`,
        method: "DELETE",
      }),
      transformResponse: () => ({ message: "Panel deleted successfully" }),
      invalidatesTags: ["DrugPanels", "Tests"],
    }),
  }),
});

export const {
  useListDrugPanelsQuery,
  useGetDrugPanelByIdQuery,
  useCreateDrugPanelMutation,
  useUpdateDrugPanelMutation,
  useDeleteDrugPanelMutation,
} = drugPanelApi;
